import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgePercent, Gavel, Trophy, Clock, Lock, Sparkles, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HISTORIC_SALES, COLLECTIONS_TICKER } from "@/lib/site-data";
import api from "@/lib/api";
import CountdownTimer from "@/components/CountdownTimer";
import TickerCarousel from "@/components/TickerCarousel";
import CountUp from "@/components/CountUp";

export default function Home() {
  const [lots, setLots] = useState([]);

  // Adaptive polling — fast (10s) when any lot is within 5 minutes of a start
  // or end transition (so "Coming Soon" flips to "Live" seamlessly at go-time),
  // slow (60s) otherwise. Ensures the homepage doesn't show stale "upcoming"
  // badges after an auction goes live.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        // Fetch both live AND upcoming lots so the homepage picks up newly-live
        // lots without a reload the moment their status flips server-side.
        const [live, up] = await Promise.all([
          api.get("/lots?status=live&limit=12").catch(() => ({ data: { items: [] } })),
          api.get("/lots?status=upcoming&limit=12").catch(() => ({ data: { items: [] } })),
        ]);
        if (cancelled) return;
        const merged = [...(live.data.items || []), ...(up.data.items || [])].slice(0, 12);
        setLots(merged);
      } catch (_) { /* ignore */ }
    };
    const pickInterval = () => {
      const now = Date.now();
      let minDelta = Infinity;
      for (const l of lots) {
        const s = new Date(l.auction_starts_at || l.starts_at || 0).getTime();
        const e = new Date(l.end_time || l.auction_ends_at || 0).getTime();
        if (s > now) minDelta = Math.min(minDelta, s - now);
        if (e > now) minDelta = Math.min(minDelta, e - now);
      }
      if (minDelta <= 5 * 60_000) return 10_000; // <5 min to a transition → fast
      return 60_000;                              // otherwise → slow
    };
    const tick = async () => {
      if (cancelled) return;
      await load();
      if (cancelled) return;
      timer = setTimeout(tick, pickInterval());
    };
    load().then(() => {
      if (cancelled) return;
      timer = setTimeout(tick, pickInterval());
    });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* EDITORIAL HERO — typographic, no banner image */}
      <section className="relative bg-white border-b border-[var(--ia-border)] overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div
          aria-hidden
          className="absolute -right-32 top-10 w-[640px] h-[120px] bg-[var(--ia-red)] hidden md:block"
          style={{ transform: "rotate(-8deg)", opacity: 0.06 }}
        />

        {/* Decorative gavel — pure SVG line-art, sits behind the headline without changing any text/numbers */}
        <div
          aria-hidden
          className="absolute right-4 md:right-12 top-6 md:top-12 pointer-events-none hidden sm:block"
          data-testid="hero-gavel"
        >
          <svg
            viewBox="0 0 220 220"
            className="w-[180px] md:w-[260px] lg:w-[300px] text-[var(--ia-ink)]"
            style={{ opacity: 0.92 }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="gavelGlow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#DC2626" stopOpacity="0.0" />
                <stop offset="1" stopColor="#DC2626" stopOpacity="0.18" />
              </linearGradient>
            </defs>
            {/* Soft glow */}
            <circle cx="148" cy="78" r="68" fill="url(#gavelGlow)" />
            {/* Sound block / base */}
            <g transform="rotate(-12 110 110)" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none">
              <rect x="22" y="160" width="120" height="14" strokeWidth="3" />
              <line x1="32" y1="178" x2="132" y2="178" strokeWidth="2" opacity="0.45" />
              {/* Handle */}
              <line x1="92" y1="62" x2="56" y2="148" strokeWidth="4" />
              {/* Gavel head */}
              <rect x="98" y="34" width="78" height="44" rx="3" strokeWidth="3" />
              <line x1="116" y1="34" x2="116" y2="78" strokeWidth="2" opacity="0.6" />
              <line x1="158" y1="34" x2="158" y2="78" strokeWidth="2" opacity="0.6" />
              {/* Tiny accent strike-spark in brand red */}
              <g stroke="#DC2626" strokeWidth="2.5" opacity="0.9">
                <line x1="178" y1="22" x2="190" y2="10" />
                <line x1="186" y1="36" x2="200" y2="34" />
                <line x1="184" y1="14" x2="190" y2="6" />
              </g>
            </g>
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-20">
          <div className="flex items-center gap-3 mb-7">
            <span className="live-dot pulse-red"></span>
            <span className="tracking-eyebrow text-[var(--ia-red)]">Incoming Auction · Accepting Consignments</span>
          </div>

          <h1 className="font-display font-black tracking-tighter leading-[0.92] text-6xl sm:text-7xl lg:text-[8.5rem] max-w-[1100px]">
            Bid on <span className="italic font-light text-[var(--ia-red)]">history.</span><br />
            Own the <span className="underline decoration-[var(--ia-red)] decoration-[6px] underline-offset-[10px]">moment.</span>
          </h1>

          <div className="mt-12 grid grid-cols-12 gap-8 items-end">
            <p className="col-span-12 md:col-span-6 text-lg md:text-xl text-[var(--ia-muted)] leading-relaxed max-w-2xl">
              Elite Sports Auction Services. Game-used &amp; event worn memorabilia, photo-matched memorabilia, sports cards, high-end autographs, and museum-grade artifacts — sold to serious collectors on a discreet, anonymous floor.
            </p>

            <div className="col-span-12 md:col-span-6 md:justify-self-end">
              <div className="grid grid-cols-3 gap-6 max-w-md">
                <Stat n={<><CountUp prefix="$" to={25} suffix="M+" /></>} label="Auction / Private Sales" />
                <Stat n={<><CountUp to={0} suffix="%" /></>} label="Seller fees · game-used" />
                <Stat n={<><CountUp to={60} suffix="s" /></>} label="Anti-snipe extension" />
              </div>
            </div>
          </div>

          {/* CTA row — three buttons: Browse, Consign, Register to Bid */}
          <div className="mt-12 flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3">
            <Link to="/auctions" data-testid="home-cta-browse" className="w-full md:w-auto">
              <Button size="lg" className="w-full md:w-auto rounded-none bg-[var(--ia-red)] hover:bg-[var(--ia-red-deep)] text-white font-semibold px-8 h-12 text-base">
                Browse Live Auctions <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/marketplace" data-testid="home-cta-browse-marketplace" className="w-full md:w-auto">
              <Button size="lg" className="w-full md:w-auto rounded-none bg-black hover:bg-[var(--ia-red)] text-white font-semibold px-8 h-12 text-base">
                Browse Marketplace <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/consign" data-testid="home-cta-consign" className="w-full md:w-auto">
              <Button size="lg" variant="outline" className="w-full md:w-auto rounded-none border-black hover:bg-black hover:text-white font-semibold px-8 h-12 text-base">
                Consign Today
              </Button>
            </Link>
            <Link to="/register" data-testid="home-cta-register" className="w-full md:w-auto">
              <Button
                size="lg"
                className="w-full md:w-auto rounded-none bg-black text-white hover:bg-[var(--ia-red)] font-semibold px-8 h-12 text-base relative group"
              >
                <Sparkles className="h-4 w-4 mr-2 text-[var(--ia-red)] group-hover:text-white transition-colors" />
                Register to Bid
              </Button>
            </Link>
          </div>

          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-3 text-xs tracking-eyebrow text-[var(--ia-muted)]">
            <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />Hard-Close + Anti-Snipe 60 Second Clock Reset</span>
            <span className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />Buy It Now Option on Every Lot</span>
            <span className="flex items-center gap-2"><Gavel className="h-3.5 w-3.5" />Straight Bids Only</span>
            <span className="flex items-center gap-2"><BadgePercent className="h-3.5 w-3.5" />0% Game-Used Seller Fees</span>
          </div>
        </div>
      </section>

      {/* OPEN LOTS — now a ticker, above Historic Sales */}
      {lots.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-8 mb-10 flex items-end justify-between">
            <div>
              <div className="tracking-eyebrow text-[var(--ia-red)] mb-2"><span className="live-dot mr-2"></span>Preview</div>
              <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Auction Lots</h2>
            </div>
            <Link to="/auctions" className="text-sm font-semibold hover:text-[var(--ia-red)] hidden md:inline-flex items-center">
              View full catalog <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          <TickerCarousel speed={36} testId="home-lots-ticker">
            {lots.map((l) => (
              <Link to={`/lots/${l.id}`} key={l.id} className="mx-0 md:mx-4 w-[260px] md:w-[320px] shrink-0 group border border-[var(--ia-border)] bg-white hover:border-[var(--ia-red)] transition-colors block">
                <div className="aspect-[4/3] overflow-hidden bg-[var(--ia-bg-soft)] relative">
                  {l.images?.[0] && (
                    <img src={l.images[0]} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  )}
                  {l.auction_status === "live" && (
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 tracking-eyebrow text-[10px] flex items-center gap-1">
                      <span className="live-dot"></span> Live
                    </div>
                  )}
                  {l.auction_status === "upcoming" && (
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 tracking-eyebrow text-[10px] flex items-center gap-1">
                      Upcoming
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="tracking-eyebrow text-[10px] text-[var(--ia-muted)] mb-1">{l.category || "Lot"}</div>
                  <div className="font-display font-bold text-sm line-clamp-2 min-h-[2.6rem]">{l.title}</div>
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[9px] tracking-eyebrow text-[var(--ia-muted)]">{l.bid_count > 0 ? "Current" : "Starting"}</div>
                      <div className="font-mono font-bold tabular-nums">${Number(l.current_price).toLocaleString()}</div>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-dashed border-[var(--ia-border)]">
                      <div className="text-[9px] tracking-eyebrow text-[var(--ia-muted)]">
                        {l.auction_status === "upcoming" ? "Starts in" : "Ends in"}
                      </div>
                      <CountdownTimer endTime={l.end_time} startTime={l.auction_starts_at} compact showLabel={false} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </TickerCarousel>
        </section>
      )}

      {/* BUYING $10M+ moved back to the features grid below */}

      {/* WE ARE BUYING — clickable banner that opens a mailto to info@ */}
      <section className="pt-16 md:pt-20 pb-4 border-t border-[var(--ia-border)]" data-testid="buying-banner-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="tracking-eyebrow text-[var(--ia-red)] mb-2">Now Buying</div>
              <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter">Photo-Matched Jerseys</h2>
            </div>
            <div className="hidden md:block text-xs tracking-eyebrow text-[var(--ia-muted)]">Michael Jordan · Kobe Bryant · Fast payment</div>
          </div>
          <a
            href="mailto:info@infiniteauctions.com?subject=I%20have%20a%20photo-matched%20jersey%20to%20sell&body=Hi%20Infinite%20Auctions%20team%2C%0A%0AI%20have%20a%20photo-matched%20game-worn%20jersey%20I%27d%20like%20to%20sell.%20Details%3A%0A%0APlayer%3A%20%0AYear%2FSeason%3A%20%0ATeam%3A%20%0APhoto-match%20source%3A%20%0AAsking%20price%20(optional)%3A%20%0A%0AI%27ll%20follow%20up%20with%20photos%20and%20provenance.%0A%0AThanks%2C"
            className="group block relative overflow-hidden border-2 border-black shadow-[10px_10px_0_#DC2626] hover:shadow-[14px_14px_0_#DC2626] transition-shadow duration-200"
            data-testid="buying-banner-link"
            aria-label="Email us about selling your photo-matched game-worn jersey"
          >
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_infinite-reimagined/artifacts/nvu2g0l5_JordanKobeBuy.png"
              alt="Infinite Auctions is buying photo-matched game-worn jerseys of Michael Jordan and Kobe Bryant. Fair offers, fast payments."
              loading="lazy"
              decoding="async"
              className="block w-full h-auto transition-transform duration-300 group-hover:scale-[1.01]"
            />
            {/* Hover CTA overlay */}
            <div className="absolute top-3 right-3 md:top-6 md:right-6 bg-[var(--ia-red)] text-white px-3 py-1.5 md:px-4 md:py-2 tracking-eyebrow text-[10px] md:text-xs shadow-lg opacity-90 group-hover:opacity-100 transition-opacity">
              Click to email us →
            </div>
          </a>
          <p className="mt-3 text-xs text-[var(--ia-muted)] text-center md:text-right">
            Have a photo-matched jersey?{" "}
            <a
              href="mailto:info@infiniteauctions.com?subject=I%20have%20a%20photo-matched%20jersey%20to%20sell"
              className="text-[var(--ia-red)] hover:underline font-semibold"
              data-testid="buying-banner-mailto-link"
            >
              info@infiniteauctions.com
            </a>
            {" "}· fair offers, fast payment, easy transaction
          </p>
        </div>
      </section>

      {/* HISTORIC SALES TICKER */}
      <section className="py-20 md:py-24 border-t border-[var(--ia-border)]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-10 flex items-end justify-between">
          <div>
            <div className="tracking-eyebrow text-[var(--ia-muted)] mb-2">The Vault</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Historic Sales</h2>
          </div>
          <div className="hidden md:block text-xs tracking-eyebrow text-[var(--ia-muted)]">A decade of record-setting hammers</div>
        </div>

        <TickerCarousel speed={42} testId="historic-sales-ticker">
          {HISTORIC_SALES.map((s, i) => (
            <div key={i} className="relic-card mx-0 md:mx-4 w-[260px] md:w-[320px] shrink-0">
              <div className="aspect-square overflow-hidden bg-white flex items-center justify-center">
                <img src={s.img} alt={s.title} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="px-4 py-3 border-t border-[var(--ia-border)] flex items-center justify-between">
                <span className="tracking-eyebrow text-[10px] text-[var(--ia-red)]">Sold</span>
                <span className="font-display font-semibold text-xs text-[var(--ia-ink)] truncate ml-3">{s.title}</span>
              </div>
            </div>
          ))}
        </TickerCarousel>
      </section>

      {/* MEDIA REACH & PLAYER COLLECTIONS — single ticker mixing media logos + player headshots */}
      <section className="py-20 md:py-24 border-t border-[var(--ia-border)] bg-[var(--ia-bg-soft)]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 mb-10">
          <div className="tracking-eyebrow text-[var(--ia-muted)] mb-2">Media Reach &amp;</div>
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Player Collections</h2>
        </div>

        <TickerCarousel speed={38} testId="player-collections-ticker">
          {COLLECTIONS_TICKER.map((item, i) => (
            <div key={i} className="mx-0 md:mx-4 w-[260px] md:w-[320px] shrink-0 bg-white border border-[var(--ia-border)]">
              {item.kind === "player" ? (
                <Link to="/auctions" className="block group">
                  <div className="aspect-square overflow-hidden bg-white">
                    <img src={item.img} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                </Link>
              ) : (
                <div className="aspect-square flex items-center justify-center bg-white p-10">
                  <img src={item.img} alt={item.name} className="max-w-full max-h-full object-contain" loading="lazy" />
                </div>
              )}
            </div>
          ))}
        </TickerCarousel>
      </section>

      {/* REMAINING FEATURES — Consign + Privacy + Buying */}
      <section className="py-20 md:py-28 border-t border-[var(--ia-border)]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-12">
          <Feature icon={<BadgePercent className="h-7 w-7" />} eyebrow="Consign" title="Better Fees. Better Results."
            body="0% seller fees on game-used items and rare autographs. A flat 10% fee on graded cards. You keep more of what you earn." />
          <Feature icon={<ShieldCheck className="h-7 w-7" />} eyebrow="Privacy" title="Built for the Private Collector"
            body="A discreet service for high-profile clients. Bidder identities are anonymized on the public auction floor — never exposed to other users." />
          <Feature icon={<Trophy className="h-7 w-7" />} eyebrow="Buying" title="$10M+ in Photo-Matched Acquisitions"
            body="We are always purchasing photo-matched game-used memorabilia from superstars, legends, and Hall of Famers. Email us today." />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-black text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <Gavel className="h-10 w-10 mx-auto text-[var(--ia-red)]" />
          <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-4">Ready to bid?</h2>
          <p className="mt-4 text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Register a free bidder account in 30 seconds. Proxy bidding lets you set your max — we&apos;ll bid for you in real time until your limit.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/register">
              <Button size="lg" className="rounded-none bg-[var(--ia-red)] hover:bg-[var(--ia-red-deep)] text-white font-semibold px-7">
                Register to Bid
              </Button>
            </Link>
            <Link to="/auctions" data-testid="cta-browse-auctions">
              <Button size="lg" variant="outline" className="rounded-none border-white text-white bg-transparent hover:bg-white hover:text-black font-semibold px-7">
                Browse Auctions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-t border-black pt-3">
      <div className="font-display font-black text-2xl md:text-3xl tracking-tighter leading-none">{n}</div>
      <div className="tracking-eyebrow text-[10px] text-[var(--ia-muted)] mt-2 leading-snug">{label}</div>
    </div>
  );
}

function Feature({ icon, eyebrow, title, body }) {
  return (
    <div className="border-l-2 border-[var(--ia-red)] pl-6">
      <div className="mb-4 text-[var(--ia-red)]">{icon}</div>
      <div className="tracking-eyebrow text-[var(--ia-muted)] mb-2">{eyebrow}</div>
      <h3 className="font-display font-bold text-2xl tracking-tight mb-3">{title}</h3>
      <p className="text-sm md:text-base text-[var(--ia-muted)] leading-relaxed">{body}</p>
    </div>
  );
}
